/**
 * The **battery** widget (`specs/M10-D2-widgets.md` §5.2) — one on this account, and monday
 * serves that one through an app iframe, so its own settings were never readable. The shape here
 * is the column registry's `battery` aggregation, which already exists for the table view's group
 * footer: a `(label, count)` bucket set drawn as a segmented bar.
 */
import { z } from 'zod';
import { defineWidget } from '../types.js';
export const batteryWidgetSettingsSchema = z.object({
    columnId: z.string().nullable().default(null),
    boardIds: z.array(z.string()).default([]),
    showPercentage: z.boolean().default(true),
    extra: z.record(z.string(), z.unknown()).default({}),
});
export const batteryWidget = defineWidget({
    id: 'battery',
    kind: 'BATTERY',
    title: 'Battery',
    description: 'How far along a status column is, as one segmented bar.',
    settingsSchema: batteryWidgetSettingsSchema,
    defaultSettings: batteryWidgetSettingsSchema.parse({}),
    dataShape: 'buckets',
    defaultLayout: { x: 0, y: 0, w: 6, h: 6 },
    minLayout: { w: 3, h: 3 },
    requiresBoards: true,
    implemented: true,
    plannedIn: null,
    galleryOrder: 3,
    fromMondaySettings(raw) {
        const source = asObject(raw);
        const known = new Set(['column_id', 'board_ids', 'show_percentage']);
        const extra = {};
        for (const [key, value] of Object.entries(source))
            if (!known.has(key))
                extra[key] = value;
        return batteryWidgetSettingsSchema.parse({
            columnId: typeof source['column_id'] === 'string' ? source['column_id'] : null,
            boardIds: Array.isArray(source['board_ids']) ? source['board_ids'].map(String) : [],
            showPercentage: source['show_percentage'] !== false,
            extra,
        });
    },
    toMondaySettings(settings) {
        const out = { column_id: settings.columnId };
        if (settings.boardIds.length > 0)
            out['board_ids'] = settings.boardIds;
        if (!settings.showPercentage)
            out['show_percentage'] = false;
        for (const [key, value] of Object.entries(settings.extra))
            out[key] = value;
        return out;
    },
    dataRequest(settings) {
        return {
            dimension: settings.columnId === null ? { kind: 'none' } : { kind: 'column', columnId: settings.columnId },
            dateBucket: 'month',
            measures: [{ key: 'count', columnId: null, func: 'count' }],
            includeEmpty: false,
            boardIds: settings.boardIds,
        };
    },
});
function asObject(raw) {
    if (typeof raw === 'string') {
        try {
            const parsed = JSON.parse(raw);
            return parsed !== null && typeof parsed === 'object' ? parsed : {};
        }
        catch {
            return {};
        }
    }
    return raw !== null && typeof raw === 'object' ? raw : {};
}
//# sourceMappingURL=index.js.map